import { forwardRef } from 'react';
import { Button } from './Button';

export interface RatingProps {
  id?: string;
  name: string;
  value: number | undefined;
  onChange: (next: number | undefined) => void;
  onBlur?: () => void;
  max?: number;
  disabled?: boolean;
  invalid?: boolean;
  /** Passed straight through from FormField's render props — its presence
   * (not its value) decides whether the keyboard-accessible "Clear" control
   * renders: a REQUIRED rating shouldn't offer a way back to "unanswered". */
  'aria-required'?: true;
}

// Native radios 1..max with sr-only inputs and a visible star glyph as each
// one's label — keyboard behaviour (arrow keys move the selection, Space/
// click selects) comes free from native same-name radios, same reasoning as
// RadioGroup. A native radio group has no built-in "deselect" gesture once a
// value is chosen, so when the field is NOT required this renders an
// explicit, keyboard-reachable "Clear" button (DS Button, ghost/sm) after
// the stars — Tab reaches it in document order, Enter/Space activates it.
export const Rating = forwardRef<HTMLInputElement, RatingProps>(function Rating(
  { id, name, value, onChange, onBlur, max = 5, disabled, invalid, ...aria },
  ref,
) {
  const isRequired = Boolean(aria['aria-required']);
  const stars = Array.from({ length: max }, (_, i) => i + 1);

  return (
    <div className="flex items-center gap-1">
      {stars.map((n) => {
        const optionId = `${id ?? name}-${n}`;
        const checked = value === n;
        return (
          <label
            key={n}
            htmlFor={optionId}
            className={`focus-within:ring-2 focus-within:ring-ring rounded text-xl leading-none ${
              disabled ? 'pointer-events-none opacity-50' : 'cursor-pointer'
            } ${checked ? 'text-warning' : 'text-muted-foreground'}`}
          >
            <input
              ref={n === 1 ? ref : undefined}
              type="radio"
              id={optionId}
              name={name}
              value={n}
              checked={checked}
              onChange={() => onChange(n)}
              onBlur={onBlur}
              disabled={disabled}
              aria-invalid={invalid || undefined}
              className="sr-only"
            />
            <span aria-hidden="true">★</span>
            <span className="sr-only">
              {n} star{n === 1 ? '' : 's'}
            </span>
          </label>
        );
      })}
      {!isRequired && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onChange(undefined)}
          disabled={disabled || value === undefined}
          className="ml-1"
        >
          Clear
        </Button>
      )}
    </div>
  );
});
